import { X, Users } from 'lucide-react';
import { useState } from 'react';

import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useAddTeamUsageCredits } from '@/hooks/useTeams';

export default function TeamUsageCreditsModal({
  isOpen,
  onClose,
  teamId,
  userId,
  rateCentsPer1000,
}: {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  userId?: number;
  rateCentsPer1000: number;
}) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const addUsageCredits = useAddTeamUsageCredits();
  const toast = useToast();

  if (!isOpen) return null;

  const dollars = parseFloat(amount);
  const creditsPreview =
    Number.isFinite(dollars) && dollars > 0
      ? Math.floor((Math.round(dollars * 100) * 1000) / rateCentsPer1000)
      : null;

  const handleClose = () => {
    setAmount('');
    setReason('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!Number.isFinite(dollars) || dollars <= 0)
      return toast.error('Invalid amount', 'Enter a positive dollar value');
    try {
      const res = await addUsageCredits.mutateAsync({
        teamId,
        userId,
        amount_usd: dollars,
        reason: reason || undefined,
      });
      toast.success(
        'Team usage credits added',
        `Purchased balance: ${res.purchased_balance} (total ${res.balance})`
      );
      handleClose();
    } catch (e) {
      toast.error(
        'Failed to add team usage credits',
        e instanceof Error ? e.message : 'Unknown error'
      );
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-3 sm:items-center"
      onClick={handleClose}
    >
      <Card
        className="my-auto w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Add team usage credits
              </CardTitle>
              <CardDescription>
                Funds the shared team credit pool. Members draw from it only
                after exhausting their own daily and monthly credits. Never
                expires (ledger source: purchase, admin-tagged).
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={handleClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1 block">
              Dollar value
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="50.00"
              className="w-full px-3 py-2 border rounded-md bg-background"
            />
            {creditsPreview !== null && (
              <p className="text-xs text-muted-foreground mt-1">
                = {creditsPreview.toLocaleString()} credits → team purchased
                balance
              </p>
            )}
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">
              Reason (optional)
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason for adjustment"
              className="w-full px-3 py-2 border rounded-md bg-background"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button
              onClick={handleSubmit}
              disabled={addUsageCredits.isPending || !amount}
              className="flex-1"
            >
              {addUsageCredits.isPending ? 'Adding...' : 'Add usage credits'}
            </Button>
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
